import { useEffect } from 'react';
import { useCookieConsent } from './useCookieConsent';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export interface AnalyticsEvent {
  event: string;
  category?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

export const useAnalytics = () => {
  const { consent } = useCookieConsent();

  const trackEvent = (eventName: string, params?: Record<string, any>) => {
    if (!consent.analytics || typeof window === 'undefined' || !window.gtag) {
      return;
    }

    window.gtag('event', eventName, params);
  };

  // Custom events for Roca Rio
  const trackBookingIntent = (excursionTitle: string, method: 'whatsapp' | 'form' | 'catalog') => {
    trackEvent('booking_intent', {
      excursion_title: excursionTitle,
      booking_method: method,
      timestamp: new Date().toISOString(),
    });
  };

  const trackViewExcursion = (excursionTitle: string, excursionId: string) => {
    trackEvent('view_excursion', {
      excursion_title: excursionTitle,
      excursion_id: excursionId,
      timestamp: new Date().toISOString(),
    });
  };

  const trackContactSubmit = (excursionTitle?: string, method?: string) => {
    trackEvent('contact_submit', {
      excursion_title: excursionTitle || 'general',
      contact_method: method || 'form',
      timestamp: new Date().toISOString(),
    });
  };

  const trackWhatsAppClick = (source: string, excursionTitle?: string) => {
    trackEvent('whatsapp_click', {
      source,
      excursion_title: excursionTitle || 'general',
      timestamp: new Date().toISOString(),
    });
  };

  const trackPageView = (pagePath: string, pageTitle: string) => {
    if (!consent.analytics || typeof window === 'undefined' || !window.gtag) {
      return;
    }

    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  };

  const trackSearch = (searchTerm: string, resultCount?: number) => {
    trackEvent('search', {
      search_term: searchTerm,
      result_count: resultCount,
    });
  };

  const trackFilterApplied = (filterType: string, filterValue: string) => {
    trackEvent('filter_applied', {
      filter_type: filterType,
      filter_value: filterValue,
    });
  };

  return {
    trackEvent,
    trackBookingIntent,
    trackViewExcursion,
    trackContactSubmit,
    trackWhatsAppClick,
    trackPageView,
    trackSearch,
    trackFilterApplied,
  };
};
